// Browser page translators (Chrome/Edge "Translate to Polish") swap every text node
// React rendered for a <font> copy holding the translation. React still holds the
// original node, so the next commit that removes it, or inserts something before it,
// throws NotFoundError ("Failed to execute 'insertBefore' on 'Node'...") and React 18
// unmounts the whole root: a white page. Seen on Add Safety Equipment — the save
// button swaps its icon for a spinner right before its (translated) label.
//
// Most of the platform is English-only, so people rely on the translator; blocking it
// with translate="no" is not an option. Instead the two DOM calls React makes are made
// tolerant of a node the translator already took away. Known workaround, see
// https://github.com/facebook/react/issues/11538.
//
// What this does NOT fix: a translated label whose text React later changes keeps
// showing the old translation, because React writes into the detached node. Wrap such
// labels in an element (<span>{label}</span>) — React then replaces the content itself.

let installed = false;

export function installTranslatorDomGuard(NodeImpl = typeof Node === 'undefined' ? null : Node) {
  if (!NodeImpl || installed) return;
  installed = true;

  const originalRemoveChild = NodeImpl.prototype.removeChild;
  NodeImpl.prototype.removeChild = function removeChild(child) {
    if (child.parentNode !== this) {
      // Already gone — the translator replaced it. Nothing left to remove.
      return child;
    }
    return originalRemoveChild.call(this, child);
  };

  const originalInsertBefore = NodeImpl.prototype.insertBefore;
  NodeImpl.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      // The anchor was swapped out, so its position is unknown. Appending keeps the new
      // node on screen (an error message, a spinner) — dropping it would hide it, and a
      // slightly wrong order is the lesser harm.
      return this.appendChild(newNode);
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  };
}
